import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchTeamMembers,
  updateMemberRole,
  removeMember,
  createTeamMember,
} from '@/lib/api/team-members'
import type { OrgRole, CreateTeamMemberFormData } from '@/types/team-members'
import { toast } from 'sonner'

/**
 * Query key factory for team members
 */
export const teamMemberKeys = {
  all: ['team-members'] as const,
  lists: () => [...teamMemberKeys.all, 'list'] as const,
  list: (organizationId: string) => [...teamMemberKeys.lists(), organizationId] as const,
}

/**
 * Fetch all team members for current organization
 */
export function useTeamMembers() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: teamMemberKeys.list(organizationId || ''),
    queryFn: () => fetchTeamMembers(organizationId!),
    enabled: !!organizationId,
  })
}

/**
 * Update a team member's role
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (data: { userId: string; role: OrgRole }) =>
      updateMemberRole(currentOrganization!.id, data.userId, data.role),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamMemberKeys.list(currentOrganization!.id),
      })
      toast.success('Member role updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update member role', {
        description: error.message,
      })
    },
  })
}

/**
 * Remove a team member from the organization
 */
export function useRemoveMember() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (userId: string) =>
      removeMember(currentOrganization!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamMemberKeys.list(currentOrganization!.id),
      })
      toast.success('Member removed successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove member', {
        description: error.message,
      })
    },
  })
}

/**
 * Create a new team member directly (with optional location assignments)
 */
export function useCreateTeamMember() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (data: CreateTeamMemberFormData) =>
      createTeamMember(currentOrganization!.id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: teamMemberKeys.list(currentOrganization!.id),
      })
      const message = response.is_new_user
        ? 'Team member created successfully. They will receive an email to set their password.'
        : 'Existing user added to your organization.'
      toast.success(message)
    },
    onError: (error: Error) => {
      toast.error('Failed to create team member', {
        description: error.message,
      })
    },
  })
}
